import { z } from "zod";
import { ActionStatus } from "./action";

// ---------------------------------------------------------------------------
// Monthly report finalize + archive (GRA-212) + draft aggregation (GRA-210)
//
// Storage: Businesses/{businessId}/monthlyReports/{month}  (month = "YYYY-MM")
//
// The collection holds two different document shapes, discriminated by
// `status`:
//  - `"draft"` (`MonthlyReportDraftRecord`): written only by the GRA-210
//    Trigger.dev job, on the 1st of the month for the previous month.
//  - `"final"` (`MonthlyReportRecord`): written only by `finalizeMonthlyReport`
//    (GRA-212's "Slutför"), the one immutable version a manager locks in. A
//    finalized doc is never updated again - `finalizeMonthlyReport` throws if
//    one already exists for the month (`MonthlyReportAlreadyFinalizedError`
//    in @graftus/db), and the GRA-210 job's `writeMonthlyReportDraft` refuses
//    to overwrite one either.
//
// Any reader of this collection must filter by `status` explicitly rather
// than assume every doc is final - see `getMonthlyReport` /
// `listFinalizedMonthlyReports` in @graftus/db.
// ---------------------------------------------------------------------------

/** "YYYY-MM". */
export const MONTH_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

/** `"draft"` is the GRA-210 job's generated draft; this module writes only `"final"`. */
export const MonthlyReportStatus = z.enum(["draft", "final"]);
export type MonthlyReportStatus = z.infer<typeof MonthlyReportStatus>;

/** Who a finalized report may be shared with - the sketch's "Dela med" choices. */
export const MonthlyReportAudience = z.enum(["merchant", "regional", "hr", "me"]);
export type MonthlyReportAudience = z.infer<typeof MonthlyReportAudience>;
/** Single source of truth for both the webapp's config card and this schema. */
export const MONTHLY_REPORT_AUDIENCES = MonthlyReportAudience.options;

/** Mirrors `ObjectiveProgressStatus` (packages/schema/src/playbook-progress.ts) as a validated wire enum. */
export const MonthlyReportKpiStatus = z.enum([
  "on_track",
  "stalling",
  "off_track",
  "no_target",
  "no_data",
]);
export type MonthlyReportKpiStatus = z.infer<typeof MonthlyReportKpiStatus>;

/**
 * One KPI line, snapshotted at finalize time so a later KPI edit or
 * recompute never changes an already-locked report.
 */
export const MonthlyReportKpiLine = z.object({
  id: z.string().min(1),
  teamId: z.string().min(1),
  teamName: z.string().min(1),
  kpiName: z.string().min(1),
  unit: z.string().default(""),
  value: z.number().nullable(),
  target: z.number().nullable(),
  status: MonthlyReportKpiStatus,
});
export type MonthlyReportKpiLine = z.infer<typeof MonthlyReportKpiLine>;

/**
 * One action-plan row. Resolved server-side from the period's accepted/done
 * Åtgärder actions (replacing the old hardcoded `plan: []` stub) - never
 * trusted from the client, so "Slutför" locks in real committed work. The
 * frontend substitutes `memberTitle` for `memberName` when "roles instead of
 * names" is on; the server always returns both.
 */
export const MonthlyReportPlanRow = z.object({
  actionId: z.string().min(1),
  scope: z.enum(["business", "team", "member"]),
  teamId: z.string().nullable(),
  teamName: z.string().nullable(),
  memberId: z.string().nullable(),
  memberName: z.string().nullable(),
  /** The roster member's job title, e.g. "Avdelningschef Färskvaror". */
  memberTitle: z.string().nullable(),
  text: z.string().min(1),
  status: z.enum(["accepted", "done"]),
});
export type MonthlyReportPlanRow = z.infer<typeof MonthlyReportPlanRow>;

export interface MonthlyReportRecord {
  /** = month. */
  id: string;
  businessId: string;
  /** "YYYY-MM". */
  month: string;
  /** Always "final" - see the module note above. */
  status: "final";
  /** The chosen teams at finalize time, snapshotted by name. Empty = every team. */
  teams: Array<{ id: string; name: string }>;
  audience: MonthlyReportAudience;
  rolesInsteadOfNames: boolean;
  /** Manager-edited (or left on the GRA-211 placeholder). Never AI-generated by this module. */
  summary: string;
  kpis: MonthlyReportKpiLine[];
  plan: MonthlyReportPlanRow[];
  finalizedBy: string;
  finalizedAt: string;
  createdAt: string | null;
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const GetMonthlyReportInput = z.object({
  businessId: z.string().min(1),
  month: z.string().regex(MONTH_REGEX),
});
export type GetMonthlyReportInput = z.infer<typeof GetMonthlyReportInput>;

export const ListMonthlyReportsInput = z.object({
  businessId: z.string().min(1),
});
export type ListMonthlyReportsInput = z.infer<typeof ListMonthlyReportsInput>;

/** The live (unlocked) action plan for a month still being reviewed. */
export const GetMonthlyReportPlanInput = z.object({
  businessId: z.string().min(1),
  month: z.string().regex(MONTH_REGEX),
  /** Empty = every team. */
  teamIds: z.array(z.string().min(1)).default([]),
});
export type GetMonthlyReportPlanInput = z.infer<typeof GetMonthlyReportPlanInput>;

export const FinalizeMonthlyReportInput = z.object({
  businessId: z.string().min(1),
  month: z.string().regex(MONTH_REGEX),
  /** Empty = every team. */
  teamIds: z.array(z.string().min(1)).default([]),
  audience: MonthlyReportAudience,
  rolesInsteadOfNames: z.boolean().default(false),
  summary: z.string().trim().min(1).max(4000),
  /** The preview's already-computed KPI lines; the server re-resolves the plan itself. */
  kpis: z.array(MonthlyReportKpiLine).default([]),
});
export type FinalizeMonthlyReportInput = z.infer<typeof FinalizeMonthlyReportInput>;

// ---------------------------------------------------------------------------
// Draft aggregation (GRA-210)
// ---------------------------------------------------------------------------

/** One week's gross profit against target - the draft's "Gross profit per week" section. */
export interface MonthlyReportWeeklyProfitRow {
  /** "YYYY-MM-DD", Monday. */
  weekStart: string;
  /** Inclusive, weekStart + 6 days. */
  weekEnd: string;
  marginKr: number | null;
  target: number | null;
}

/** The draft's "02 Team by department" section - `kpis` regrouped by team. */
export interface MonthlyReportDepartmentRow {
  teamId: string;
  teamName: string;
  kpis: MonthlyReportKpiLine[];
}

/** Check-in (pulse) mood counts for the month, gated by `gateCheckoutDistribution`. */
export interface MonthlyReportCheckoutDistribution {
  /** Distinct members who submitted a check-in inside the month; gates the whole section. */
  k: number;
  red: number;
  yellow: number;
  green: number;
}

/**
 * Minimum respondents before a check-in distribution is shown, so a tiny
 * group's mood is never individually identifiable from the aggregate.
 */
export const MIN_CHECKOUT_DISTRIBUTION_K = 3;

/**
 * The k-anonymity gate GRA-210's acceptance criteria calls for: null hides
 * the whole section below the threshold. Pure and reusable by any future
 * "is this aggregate too small to show" check.
 */
export function gateCheckoutDistribution(
  k: number,
  distribution: { red: number; yellow: number; green: number },
): MonthlyReportCheckoutDistribution | null {
  if (k < MIN_CHECKOUT_DISTRIBUTION_K) return null;
  return { k, ...distribution };
}

// ---------------------------------------------------------------------------
// Draft action plan + summary (GRA-211)
// ---------------------------------------------------------------------------

/**
 * One row of the draft's "04 Action plan for next month" - a suggestion
 * resolved from an existing open (proposed/accepted) Åtgärd action, carrying
 * the AiMark in the review UI until a manager acts on it. Unlike
 * `MonthlyReportPlanRow` (the *final* report's backward-looking record of
 * actions acted on *during* the month), this is forward-looking and never
 * creates anything: `actionId` always points at a real, already-existing
 * action - accepting the suggestion means accepting that action, nothing is
 * ever created by this job (see the module note on `MonthlyReportDraftRecord`).
 */
export interface MonthlyReportSuggestedPlanRow {
  actionId: string;
  title: string;
  scope: "business" | "team" | "member";
  teamId: string | null;
  teamName: string | null;
  memberId: string | null;
  memberName: string | null;
  /** The roster member's job title - the "role" half of "owner (role or name)". */
  memberTitle: string | null;
  /** "YYYY-MM-DD" - the last day of the month this plan targets. */
  dueDate: string;
  /** The KPI definition's name this action targets, when it has one. */
  kpiName: string | null;
}

/**
 * The outcome of one row from *last* month's suggested plan - resolved from
 * the same action's *current* status a month later, never re-derived from
 * the stale draft, so a row shows what actually happened, not what was
 * merely proposed.
 */
export interface MonthlyReportPlanFollowUpRow {
  actionId: string;
  title: string;
  owner: string | null;
  status: ActionStatus;
  outcomeNote: string | null;
  completedAt: string | null;
}

/**
 * One number the AI summary cites, grounded in a ctx-fact key the draft's own
 * computed sections can answer - mirrors `InsightNumber`'s calc-key pattern
 * (packages/schema/src/insight.ts). See `buildMonthlyReportSummaryFacts` /
 * `guardMonthlyReportSummary` in @graftus/insights: a number whose `calc` key
 * doesn't resolve, or doesn't match, fails the guard and the summary is
 * dropped rather than persisted with an unverifiable claim.
 */
export const MonthlyReportSummaryNumber = z.object({
  /** The value as stated in the summary's text. */
  value: z.number(),
  unit: z.enum(["percent", "count", "currency", "raw"]).default("raw"),
  /** Ctx-fact key this value is claimed to equal. */
  calc: z.string().min(1),
  /** Short label for logging/debugging. */
  label: z.string().min(1),
});
export type MonthlyReportSummaryNumber = z.infer<typeof MonthlyReportSummaryNumber>;

/** Structured output schema the summary generator's model call returns. */
export const MonthlyReportSummaryOutput = z.object({
  summary: z.string().trim().min(1).max(2000),
  /** Every number the text cites - checked against the draft's own sections before persisting. */
  numbers: z.array(MonthlyReportSummaryNumber).default([]),
});
export type MonthlyReportSummaryOutput = z.infer<typeof MonthlyReportSummaryOutput>;

/**
 * Prompt version for the GRA-211 summary generator - bump on every meaningful
 * prompt change (mirrors `PROMPT_VERSION` in the chat system).
 */
export const MONTHLY_REPORT_SUMMARY_PROMPT_VERSION = 1;

/**
 * The GRA-210/211 job's generated draft (`status: "draft"`). Never includes
 * personality/assessment data - built only from KPI status, store measures,
 * pulse check-ins and the business's own open actions/insights. `plan`,
 * `planFollowUp` and `summary` are suggestions only - nothing here ever
 * creates or mutates an `actions` document; a row only becomes committed work
 * once a manager finalizes the report (GRA-212's "Slutför").
 */
export interface MonthlyReportDraftRecord {
  /** = month. */
  id: string;
  businessId: string;
  /** "YYYY-MM". */
  month: string;
  status: "draft";
  /** 01 Summary KPI table - one row per (team, active KPI) with a nightly status row. */
  kpis: MonthlyReportKpiLine[];
  /**
   * Gross profit per week against target, previous month only. Empty when
   * the business has no store (no root sales team) or no report-upload data.
   */
  weeklyProfit: MonthlyReportWeeklyProfitRow[];
  /** 02 Team by department. */
  departments: MonthlyReportDepartmentRow[];
  /** Null when fewer than MIN_CHECKOUT_DISTRIBUTION_K members checked out this month. */
  checkoutDistribution: MonthlyReportCheckoutDistribution | null;
  /** 04 Action plan for next month (GRA-211) - suggestions, AiMark until finalized. */
  plan: MonthlyReportSuggestedPlanRow[];
  /** Outcome of *last* month's suggested plan rows (GRA-211). */
  planFollowUp: MonthlyReportPlanFollowUpRow[];
  /** AI-written summary (GRA-211) - every number in it is grounded in the sections above. */
  summary: string;
  /** Prompt version that produced `summary`; null while it is still "" (GRA-210 default, or the guard rejected it). */
  summaryPromptVersion: number | null;
  summaryModelId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}
